import React, { useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import {
  AppAdminFormLabel,
  AppButton,
  AppCheckbox,
  AppForm,
  AppInputNumber,
  AppInputTime,
  AppSpin,
} from '@/components/atoms';
import { ChevronLeft } from '@/components/atoms/AppIcon';
import VisitClinicLocationField, {
  CLINIC_LOCATION_PLACEHOLDER,
} from '@/components/molecules/VisitClinicLocationField';
import VisitPatientHeader from '@/components/molecules/VisitPatientHeader';
import VisitAddressRow from '@/components/molecules/VisitAddressRow';
import VisitSuddenFields from '@/components/molecules/VisitSuddenFields';
import VisitMemoField from '@/components/molecules/VisitMemoField';
import { useMassagerAuthContext } from '@shared/providers/MassagerAuthContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useDeleteVisit, useUpdateVisitRemarks } from '@/hooks/massager/useVisitMutations';
import { useVisitList } from '@/hooks/massager/useVisitList';
import { useMassagerClinicLocations } from '@/hooks/massager/useMassagerClinicLocations';
import { usePatientAddresses } from '@/hooks/massager/usePatientLookup';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { useStandardDeleteConfirm } from '@/hooks/useStandardDeleteConfirm';
import { DEFAULT_FETCH_ERROR_MESSAGE, getApiErrorMessage, isNotFoundError } from '@/utils/apiError';
import { formatHHmm } from '@/utils/visitTimeFormat';
import { precheckSuddenVisit, showVisit } from '@/services/massager/visit.service';
import { MASSAGER_JA } from '@/shared/i18n/massager';
import { convertWarekiDetail } from '@/utils/wareki';
import dayjs, { APP_TIMEZONE } from '@shared/lib/dayjs';
import type { Dayjs } from '@shared/lib/dayjs';
import type { VisitResponse } from '@/types/massager/visit';
import {
  massagerEditVisitFormSchema,
  type MassagerEditVisitFormData,
} from '@/schemas/massagerEditVisitSchema';

function formatWarekiDate(d: Dayjs): string {
  const era = convertWarekiDetail(d.year(), d.month() + 1, d.date());
  const month = String(d.month() + 1).padStart(2, '0');
  const day = String(d.date()).padStart(2, '0');
  return `${era}年${month}月${day}日`;
}

const TIME_REGEX = /^\d{2}:\d{2}$/;

// Display order of validatable fields. The single banner above the save
// button shows the first non-empty error following this sequence so users
// see one message at a time matching the visual top-to-bottom layout.
const FIELD_ORDER: (keyof MassagerEditVisitFormData)[] = [
  'visit_time',
  'duration',
  'clinic_location_id',
  'medical_institution_name',
  'doctor_name',
  'sudden_reason',
  'coordination_date',
];

// 突発的な往療 block: the 4 fields share one generic banner message
// (必須項目を入力して下さい。) instead of their field-specific zod messages.
const SUDDEN_FIELD_LIST = [
  'medical_institution_name',
  'doctor_name',
  'sudden_reason',
  'coordination_date',
] as const satisfies readonly (keyof MassagerEditVisitFormData)[];

const SUDDEN_FIELDS: ReadonlySet<keyof MassagerEditVisitFormData> = new Set(SUDDEN_FIELD_LIST);

const SUDDEN_BLOCK_MESSAGE = '必須項目を入力して下さい。';

/**
 * /{site}/massager/checkin/remarks/:visitId
 *
 * Edit screen for a single visit. Service type + date come in via
 * `location.state` from the list page; on direct/refresh load (no state) we
 * fall back to fetching by id only — backend defaults to R2, retry with A2
 * on 404 — and derive both values from the visit response.
 *
 * Field UI is split into focused molecules (VisitClinicLocationField,
 * VisitPatientHeader, VisitAddressRow, VisitSuddenFields, VisitMemoField)
 * per the project's atomic-design rule.
 */
type RemarksNavState = { serviceType?: 'A2' | 'R2'; date?: string };

const CheckinRemarksPage: React.FC = () => {
  const { visitId, site } = useParams<{ visitId: string; site: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useAppToast();
  const appModal = useAppModal();

  const { clinic } = useMassagerAuthContext();
  const { colors } = useTheme();
  const siteType = clinic?.site_type ?? site ?? 'medix';

  const navState = (location.state ?? {}) as RemarksNavState;
  const initialServiceType: 'A2' | 'R2' | undefined =
    navState.serviceType === 'A2' || navState.serviceType === 'R2'
      ? navState.serviceType
      : undefined;
  const parsedId = Number(visitId);

  const { data, isLoading, isError, error } = useQuery<VisitResponse, Error>({
    queryKey: ['massager', 'visits', siteType, 'show', parsedId],
    queryFn: async () => {
      try {
        return await showVisit(siteType, parsedId, initialServiceType);
      } catch (err) {
        // Refresh / direct-load: state is empty → showVisit hits the R2 table
        // (backend default). If the visit is in the A2 table we get 404; retry
        // with service_type=A2 before bubbling the error.
        if (!initialServiceType && isNotFoundError(err)) {
          return await showVisit(siteType, parsedId, 'A2');
        }
        throw err;
      }
    },
    enabled: !!siteType && Number.isFinite(parsedId) && parsedId > 0,
  });

  const visit = data?.data;

  // Real service type comes from the response once visit is loaded; fall
  // back to navigate-state value (or undefined on refresh) until then.
  const effectiveServiceType: 'A2' | 'R2' | undefined =
    (visit?.service_type === 'A2' || visit?.service_type === 'R2'
      ? visit.service_type
      : undefined) ?? initialServiceType;

  // Date: prefer state (avoids mid-night-edge drift from visit_datetime_from);
  // fallback parse from response in Asia/Tokyo. null while visit is loading on
  // refresh — dependent hooks below skip until it resolves.
  const dateParam: string | null =
    navState.date ??
    (visit?.visit_datetime_from
      ? dayjs(visit.visit_datetime_from).tz(APP_TIMEZONE).format('YYYY-MM-DD')
      : null);

  // Other visits on the same date (used for the overlap rule on submit).
  const { visits: sameDayVisits } = useVisitList(dateParam, effectiveServiceType);

  // Active 施設 set for this massager on the visit's date — same source as the
  // AddVisitCard dropdown so the choices stay consistent across screens.
  const { locations, refetch: refetchLocations } = useMassagerClinicLocations({
    date: dateParam,
    serviceType: effectiveServiceType ?? null,
  });

  // Patient consent flags. Same source as AddVisitCard.
  const { hasElectricTreatment, suddenlyVisitIs } = usePatientAddresses(
    visit?.patient_id ?? null,
    dateParam ?? '',
    effectiveServiceType ?? null
  );

  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    clearErrors,
    getValues,
    watch,
    formState: { errors },
  } = useTrimmedForm<MassagerEditVisitFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(massagerEditVisitFormSchema),
    defaultValues: {
      visit_time: '',
      duration: null,
      clinic_location_id: undefined,
      is_not_hot_treatment: false,
      is_sudden: false,
      medical_institution_name: '',
      doctor_name: '',
      sudden_reason: '',
      coordination_date: null,
      remarks: '',
    },
  });

  useEffect(() => {
    if (!visit) return;
    const from = dayjs(visit.visit_datetime_from).tz(APP_TIMEZONE);
    const to = dayjs(visit.visit_datetime_to).tz(APP_TIMEZONE);
    reset({
      visit_time: from.isValid() ? from.format('HH:mm') : '',
      duration: from.isValid() && to.isValid() ? to.diff(from, 'minute') : null,
      clinic_location_id: visit.clinic_location_id ?? undefined,
      is_not_hot_treatment: visit.is_not_hot_treatment ?? false,
      is_sudden: !!visit.is_suddenly_visit,
      medical_institution_name: visit.hospital_name ?? '',
      doctor_name: visit.doctor_name ?? '',
      sudden_reason: visit.sudden_reason ?? '',
      coordination_date: visit.coordination_date ? dayjs(visit.coordination_date) : null,
      remarks: visit.massager_remarks ?? '',
    });
  }, [visit, reset]);

  const locationOptions = useMemo(
    () => [
      { value: CLINIC_LOCATION_PLACEHOLDER, label: '選択してください' },
      ...locations.map(l => ({
        value: l.clinic_location_id,
        label: l.address ? `${l.location_name} ー ${l.address}` : l.location_name,
      })),
    ],
    [locations]
  );

  // Any keystroke clears EVERY RHF error — banner disappears entirely
  // until the next submit. Avoids the surprise where fixing one field
  // auto-surfaces a different field's pre-existing error in the banner.
  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (!name) return;
      clearErrors();
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  const updateMut = useUpdateVisitRemarks();
  const deleteMut = useDeleteVisit();
  const openDeleteConfirm = useStandardDeleteConfirm();
  const disabled = !visit || visit.is_locked;

  useEffect(() => {
    if (!isError) return;
    if (deleteMut.isPending || deleteMut.isSuccess) return;
    toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, error, deleteMut.isPending, deleteMut.isSuccess]);

  /**
   * 削除 button (HIT-2329):
   *   1. Standard delete confirm — title 削除確認, red message
   *      「本当に削除してもよろしいですか？」, OK button danger styled.
   *   2. On OK → soft-delete; on success bounce to the visit list with the
   *      spec toast 「更新が正常に終了しました。」.
   *   3. Concurrent-lock cases — stay on page:
   *      - This visit was locked in another tab between page load and click →
   *        backend returns 403 → 「この受診者はロックされています。更新ができませんでした。」
   *      - Another visit in the same month was locked between page load and
   *        click → backend returns 422 with `is_locked` →
   *        「同月内にロック済みの訪問があります。」
   */
  const handleDelete = () => {
    if (!visit) return;
    const listPath = `/${siteType}/massager/home/${dateParam}`;

    openDeleteConfirm({
      onOk: () =>
        new Promise<void>(resolve => {
          deleteMut.mutate(
            { visitId: visit.id, serviceType: effectiveServiceType ?? null },
            {
              onSuccess: () => {
                toast.success('更新が正常に終了しました。');
                navigate(listPath, { replace: true });
                resolve();
              },
              onError: err => {
                const status = (err as { response?: { status?: number } }).response?.status;
                const apiErrors = (
                  err as { response?: { data?: { errors?: Record<string, string[]> } } }
                ).response?.data?.errors;

                // Already deleted in another tab → 404. Treat as success per
                // the same pattern used by updateRemarks: bounce back to the
                // list with the soft-delete error message.
                if (isNotFoundError(err) || status === 404) {
                  toast.error('更新ができませんでした。');
                  navigate(listPath, { replace: true });
                  resolve();
                  return;
                }
                // This visit itself was locked in another tab (assertNotLocked
                // on backend → AuthorizationException → 403). Mirror the
                // updateRemarks flow's spec message; stay on page.
                if (status === 403) {
                  toast.error('この受診者はロックされています。更新ができませんでした。');
                  resolve();
                  return;
                }
                // Same-month lock — spec message, stay on page.
                if (status === 422 && apiErrors?.is_locked?.length) {
                  toast.error('同月内にロック済みの訪問があります。');
                  resolve();
                  return;
                }
                toast.error(getApiErrorMessage(err, '更新が失敗しました。'));
                resolve();
              },
            }
          );
        }),
    });
  };

  // Overlap rule: visit time + duration must not collide with another visit
  // on the same day. Sets inline errors on both visit_time / duration and
  // returns true when an overlap was found.
  const applyOverlapCheck = (formData: MassagerEditVisitFormData): boolean => {
    if (!visit) return false;
    if (
      !formData.visit_time ||
      typeof formData.duration !== 'number' ||
      formData.duration < 1 ||
      !TIME_REGEX.test(formData.visit_time)
    ) {
      return false;
    }
    const baseDate = dayjs(visit.visit_datetime_from).tz(APP_TIMEZONE).format('YYYY-MM-DD');
    const startMs = dayjs.tz(`${baseDate} ${formData.visit_time}:00`, APP_TIMEZONE).valueOf();
    const endMs = startMs + formData.duration * 60_000;
    const overlap = sameDayVisits.some(v => {
      if (v.id === visit.id) return false;
      const vs = dayjs(v.visit_datetime_from).valueOf();
      const ve = dayjs(v.visit_datetime_to).valueOf();
      return startMs < ve && endMs > vs;
    });
    if (!overlap) return false;
    const overlapMessage = '指定された訪問日時には別の受診者がすでに登録されています。';
    setError('visit_time', { type: 'overlap', message: overlapMessage });
    setError('duration', { type: 'overlap', message: overlapMessage });
    return true;
  };

  const onValid = async (formData: MassagerEditVisitFormData) => {
    if (!visit) return;
    clearErrors();

    if (applyOverlapCheck(formData)) return;
    if (formData.clinic_location_id == null) return;

    // The location list was loaded at mount; between then and submit the
    // chosen 施設 could have been soft-deleted (clinic_locations.deleted_at)
    // or the massager removed from CLM (clinic_location_massagers.deleted_at).
    // Refetch the active set; on mismatch we toast + reset the dropdown to
    // the first fresh option (mirrors AddVisitCard's safeguard).
    const refetched = await refetchLocations();
    const freshList = refetched.data?.data?.clinic_locations ?? [];
    const stillValid = freshList.some(l => l.clinic_location_id === formData.clinic_location_id);
    if (!stillValid) {
      toast.error('指定された施設は存在しません。他の施設を選んでください。');
      const firstId = freshList[0]?.clinic_location_id;
      setValue('clinic_location_id', firstId ?? undefined, { shouldValidate: false });
      return;
    }

    const baseDate = dayjs(visit.visit_datetime_from).tz(APP_TIMEZONE).format('YYYY-MM-DD');
    const visitDatetimeFrom = `${baseDate} ${formData.visit_time}:00`;

    // 突発的な往療 precheck — runs whenever the form has is_sudden=true.
    // We pass exclude_visit_id so the BE drops this same visit from the
    // 14-day window / same-month count, otherwise an already-sudden visit
    // would self-match and falsely block.
    if (formData.is_sudden) {
      let pre;
      try {
        pre = await precheckSuddenVisit(siteType, {
          patient_id: visit.patient_id,
          visit_datetime_from: visitDatetimeFrom,
          exclude_visit_id: visit.id,
          service_type: effectiveServiceType,
        });
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'チェックに失敗しました。'));
        return;
      }
      const result = pre.data;
      if (result.blocked && result.block_message) {
        appModal.error({
          type: 'error',
          content: <div className="whitespace-pre-line">{result.block_message}</div>,
          okText: 'OK',
        });
        return;
      }
      if (result.warn_message) {
        const confirmed = await new Promise<boolean>(resolve => {
          appModal.warning({
            type: 'warning',
            content: <div className="whitespace-pre-line">{result.warn_message}</div>,
            okText: 'OK',
            onOk: () => resolve(true),
            afterClose: () => resolve(false),
          });
        });
        if (!confirmed) return;
      }
    }

    const listPath = `/${siteType}/massager/home/${dateParam}`;
    updateMut.mutate(
      {
        visitId: visit.id,
        remarks: formData.remarks,
        clinicLocationId: formData.clinic_location_id,
        visitDatetimeFrom,
        visitMinutes: formData.duration ?? 0,
        isNotHotTreatment: formData.is_not_hot_treatment,
        sudden: {
          isSudden: formData.is_sudden,
          hospitalName: formData.medical_institution_name || null,
          doctorName: formData.doctor_name || null,
          suddenReason: formData.sudden_reason || null,
          coordinationDate: formData.coordination_date
            ? formData.coordination_date.format('YYYY-MM-DD')
            : null,
        },
        serviceType: effectiveServiceType ?? null,
      },
      {
        onSuccess: () => {
          toast.success('更新が正常に終了しました。');
          navigate(listPath, { replace: true });
        },
        onError: err => {
          const status = (err as { response?: { status?: number } }).response?.status;
          const apiErrors = (err as { response?: { data?: { errors?: Record<string, string[]> } } })
            .response?.data?.errors;

          // Visit deleted in another tab → 404. Toast + bounce back to list.
          if (isNotFoundError(err) || status === 404) {
            toast.error('更新ができませんでした。');
            navigate(listPath, { replace: true });
            return;
          }
          // Visit was locked in another tab between page load and submit
          // (assertNotLocked → AuthorizationException → 403).
          if (status === 403) {
            toast.error('この受診者はロックされています。更新ができませんでした。');
            return;
          }
          // Same-month-lock guard from the backend uses the `is_locked`
          // validation key so we can distinguish it from other 422s.
          if (status === 422 && apiErrors?.is_locked?.length) {
            toast.error('同月内にロック済みの訪問があります。');
            return;
          }
          toast.error(getApiErrorMessage(err, '更新が失敗しました。'));
        },
      }
    );
  };

  const onInvalid = () => {
    const overlapped = applyOverlapCheck(getValues());
    if (overlapped) return;
    toast.error('エラーが発生しました。');
  };

  // The form surfaces one error at a time: the first field in FIELD_ORDER
  // that has a non-empty message owns both the banner text AND the red
  // border. Not memoized: RHF can mutate the `errors` object in place
  // without changing its reference, which would leave a useMemo result
  // stale and freeze the banner on a field the user has already fixed.
  let firstErrorField: keyof MassagerEditVisitFormData | null = null;
  for (const key of FIELD_ORDER) {
    const msg = errors[key]?.message;
    if (typeof msg === 'string' && msg.length > 0) {
      firstErrorField = key;
      break;
    }
  }

  const firstFieldErrorMessage = firstErrorField
    ? SUDDEN_FIELDS.has(firstErrorField)
      ? SUDDEN_BLOCK_MESSAGE
      : (errors[firstErrorField]?.message ?? null)
    : null;

  const isFirstError = (name: keyof MassagerEditVisitFormData) => firstErrorField === name;

  // Same clear-all rule as the watch subscription — every onChange in the
  // sudden block wipes the banner state form-wide. Helper name kept for
  // the existing prop contract with VisitSuddenFields.
  const clearSuddenErrors = () => clearErrors();

  if (isLoading || !visit) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <AppSpin />
      </div>
    );
  }

  const dateForDisplay = dayjs(visit.visit_datetime_from).tz(APP_TIMEZONE);
  const statusText = MASSAGER_JA.status[visit.status];
  const mapAddress = [visit.patient_address1, visit.patient_address2].filter(Boolean).join(' ');
  const mapUrl = mapAddress
    ? `https://www.google.co.jp/maps?q=${encodeURIComponent(mapAddress)}`
    : null;
  const suddenSectionVisible =
    suddenlyVisitIs || !!visit.is_suddenly_visit || visit.is_suddenly_eligible_at_creation;
  const checkboxLabel = effectiveServiceType === 'A2' ? '電(鍼・温)無し' : '温罨法・電療無し';

  return (
    <div className="flex flex-1 flex-col">
      {/* Top bar: back link (left) + delete (right) + bottom divider */}
      <div className="-mx-3 flex items-center justify-between gap-2 border-b border-gray-200 px-3 pb-2 sm:-mx-4 sm:px-4">
        <Link
          to={`/${siteType}/massager/home/${dateParam}`}
          style={{ color: colors.primary }}
          className="inline-flex items-center gap-1 text-sm hover:underline"
        >
          <ChevronLeft />
          {formatWarekiDate(dayjs(dateParam))}訪問一覧へ
        </Link>
        <AppButton
          type="primary"
          danger
          disabled={disabled}
          loading={deleteMut.isPending}
          onClick={handleDelete}
        >
          削除
        </AppButton>
      </div>

      <div className="mt-4 flex flex-col gap-4 rounded-lg bg-white p-4 shadow-sm">
        {/* Date + status (clock icon + 令和XX年MM月DD日 + status text) */}
        <div className="flex items-center gap-2 text-base text-gray-900">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-lg text-amber-600">
            <i className="fa fa-clock-o" aria-hidden="true" />
          </span>
          <span className="font-medium">
            {formatWarekiDate(dateForDisplay)} {statusText}
          </span>
        </div>

        {/* Time + duration — labels stacked above each input, two columns
            kept side-by-side on every viewport (per HIT-2329 spec). Both
            surface the page-level overlap error via setError on either
            field, hence the inline error spans below. */}
        <div className="grid grid-cols-2 items-start gap-3 sm:gap-4">
          <Controller
            name="visit_time"
            control={control}
            render={({ field }) => (
              <div className="flex min-w-0 flex-col gap-1">
                <AppAdminFormLabel label="訪問時間" required className="!mb-0" />
                <AppForm.Item
                  validateStatus={isFirstError('visit_time') ? 'error' : undefined}
                  className="!mb-0"
                >
                  <AppInputTime
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={disabled}
                    error={isFirstError('visit_time')}
                    focusColor={colors.primary}
                  />
                </AppForm.Item>
              </div>
            )}
          />

          <Controller
            name="duration"
            control={control}
            render={({ field }) => (
              <div className="flex min-w-0 flex-col gap-1">
                <AppAdminFormLabel label="所要時間" required className="!mb-0" />
                <AppForm.Item
                  validateStatus={isFirstError('duration') ? 'error' : undefined}
                  className="!mb-0"
                >
                  <div className="flex items-center gap-2">
                    <AppInputNumber
                      value={field.value ?? null}
                      onChange={v => {
                        if (v == null || v === '') {
                          field.onChange(null);
                          return;
                        }
                        const n = typeof v === 'number' ? v : Number(v);
                        field.onChange(Number.isFinite(n) ? n : null);
                      }}
                      onBlur={field.onBlur}
                      min={0}
                      max={999}
                      precision={0}
                      controls={false}
                      disabled={disabled}
                      className="!min-w-0 !flex-1"
                    />
                    <span className="shrink-0 text-sm text-gray-800">分</span>
                  </div>
                </AppForm.Item>
              </div>
            )}
          />
        </div>

        <VisitClinicLocationField
          isLocked={visit.is_locked}
          lockedName={visit.clinic_location_name}
          lockedAddress={visit.clinic_location_address}
          options={locationOptions}
          control={control}
          isFirstError={isFirstError}
        />

        <VisitPatientHeader
          patientName={visit.patient_name}
          consentAlert={visit.consent_alert}
          consentTermTo={visit.consent_term_to}
        />

        {/* 電療無し checkbox — visibility follows the patient's current
            consent (hasElectricTreatment from usePatientAddresses). */}
        {hasElectricTreatment && (
          <Controller
            name="is_not_hot_treatment"
            control={control}
            render={({ field }) => (
              <AppCheckbox
                checked={!!field.value}
                onChange={e => field.onChange(e.target.checked)}
                disabled={disabled}
                className="self-start"
              >
                {checkboxLabel}
              </AppCheckbox>
            )}
          />
        )}

        <VisitSuddenFields
          control={control}
          visible={suddenSectionVisible}
          expanded={!!watch('is_sudden')}
          disabled={disabled}
          isFirstError={isFirstError}
          clearSuddenErrors={clearSuddenErrors}
        />

        <VisitAddressRow address={visit.patient_address} mapUrl={mapUrl} />

        {firstFieldErrorMessage && (
          <p className="mt-1 whitespace-pre-line text-base font-medium text-red-500">
            {firstFieldErrorMessage}
          </p>
        )}

        {visit.is_special_area_addition && (
          <span className="text-sm font-semibold text-rose-600">特別地域加算</span>
        )}

        {visit.massager_started_at && (
          <div className="text-sm text-gray-600">
            {MASSAGER_JA.label.actualTime} {formatHHmm(visit.massager_started_at)} から
            {visit.massager_ended_at ? ` ${formatHHmm(visit.massager_ended_at)}` : ''}
          </div>
        )}

        <VisitMemoField control={control} disabled={disabled} />
      </div>

      {/* Sticky save button */}
      <div className="sticky bottom-0 z-[1100] -mx-3 -mb-4 mt-auto border-t border-gray-200 bg-white px-3 py-3 shadow-[0_-2px_6px_rgba(0,0,0,0.04)] sm:static sm:z-auto sm:mx-0 sm:mb-0 sm:mt-0 sm:border-none sm:bg-transparent sm:p-0 sm:shadow-none">
        <AppButton
          block
          type="primary"
          size="large"
          disabled={disabled}
          loading={updateMut.isPending}
          onClick={handleSubmit(onValid, onInvalid)}
          className="!min-h-11"
        >
          {MASSAGER_JA.action.save}
        </AppButton>
      </div>
    </div>
  );
};

export default CheckinRemarksPage;
